import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { createCandidate } from '@/lib/api';
import { normalizeOptionalUrlInput } from '@/lib/url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SkillsMultiSelect } from '@/components/ui/skills-multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const JOB_TYPE_OPTIONS = ['Full Time', 'Part Time', 'Contract', 'Internship'] as const;
const SOURCE_OPTIONS = ['manual', 'referral', 'linkedin', 'job_board', 'website', 'whatsapp'] as const;

const candidateSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  whatsappPhone: z.string().min(10, 'Valid phone number is required').max(20),
  email: z.string().email('Invalid email').max(255).optional().or(z.literal('')),
  phoneSecondary: z.string().max(20).optional(),
  currentLocation: z.string().max(255).optional(),
  nationality: z.string().max(100).optional(),
  yearsExperience: z.coerce.number().min(0).optional().or(z.literal('')),
  currentJobTitle: z.string().max(255).optional(),
  currentEmployer: z.string().max(255).optional(),
  expectedSalaryRange: z.string().max(100).optional(),
  availabilityDate: z.string().optional(),
  linkedinUrl: z.preprocess(
    normalizeOptionalUrlInput,
    z.string().url('Enter a valid LinkedIn URL').max(500).optional(),
  ),
  source: z.string().max(100).default('manual'),
});

type CandidateFormData = z.infer<typeof candidateSchema>;

export function CreateCandidatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<string[]>([]);
  const [preferredJobTypes, setPreferredJobTypes] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      whatsappPhone: '',
      email: '',
      phoneSecondary: '',
      currentLocation: '',
      nationality: '',
      currentJobTitle: '',
      currentEmployer: '',
      expectedSalaryRange: '',
      availabilityDate: '',
      linkedinUrl: '',
      source: 'manual',
    },
  });

  // Radix Select fields are not native inputs, so register them explicitly.
  useEffect(() => {
    register('source');
  }, [register]);

  const createMutation = useMutation({
    mutationFn: createCandidate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      navigate(`/candidates/${data.id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create candidate');
    },
  });

  const onSubmit = (data: CandidateFormData) => {
    setError(null);
    createMutation.mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      whatsappPhone: data.whatsappPhone,
      email: data.email || undefined,
      phoneSecondary: data.phoneSecondary || undefined,
      currentLocation: data.currentLocation || undefined,
      nationality: data.nationality || undefined,
      yearsExperience: data.yearsExperience === '' ? undefined : data.yearsExperience as number | undefined,
      currentJobTitle: data.currentJobTitle || undefined,
      currentEmployer: data.currentEmployer || undefined,
      skills,
      preferredJobTypes,
      expectedSalaryRange: data.expectedSalaryRange || undefined,
      availabilityDate: data.availabilityDate || undefined,
      linkedinUrl: data.linkedinUrl || undefined,
      source: data.source || 'manual',
    });
  };

  const toggleJobType = (type: string) => {
    setPreferredJobTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <Link
          to="/candidates"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidates
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-text-primary">Add New Candidate</h1>
        <p className="text-text-secondary mt-1">
          Create a new candidate profile
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => setError('Please check the form for errors before saving.'))} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Personal Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  placeholder="e.g., John"
                  {...register('firstName')}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  placeholder="e.g., Doe"
                  {...register('lastName')}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="whatsappPhone">WhatsApp Phone *</Label>
                <Input
                  id="whatsappPhone"
                  placeholder="+27 12 345 6789"
                  {...register('whatsappPhone')}
                />
                {errors.whatsappPhone && (
                  <p className="text-sm text-red-600">{errors.whatsappPhone.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phoneSecondary">Secondary Phone</Label>
                <Input
                  id="phoneSecondary"
                  placeholder="Optional"
                  {...register('phoneSecondary')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nationality">Nationality</Label>
                <Input
                  id="nationality"
                  placeholder="e.g., South African"
                  {...register('nationality')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentLocation">Current Location</Label>
                <Input
                  id="currentLocation"
                  placeholder="e.g., Cape Town, South Africa"
                  {...register('currentLocation')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
                <Input
                  id="linkedinUrl"
                  type="url"
                  placeholder="linkedin.com/in/..."
                  {...register('linkedinUrl')}
                />
                {errors.linkedinUrl && (
                  <p className="text-sm text-red-600">{errors.linkedinUrl.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Professional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentJobTitle">Current Job Title</Label>
                <Input
                  id="currentJobTitle"
                  placeholder="e.g., Senior Developer"
                  {...register('currentJobTitle')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currentEmployer">Current Employer</Label>
                <Input
                  id="currentEmployer"
                  placeholder="e.g., Acme Corp"
                  {...register('currentEmployer')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="yearsExperience">Years of Experience</Label>
              <Input
                id="yearsExperience"
                type="number"
                min="0"
                placeholder="e.g., 5"
                {...register('yearsExperience')}
              />
            </div>

            <div className="space-y-2">
              <Label>Skills</Label>
              <SkillsMultiSelect skills={skills} onSkillsChange={setSkills} />
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Preferred Job Types</Label>
              <div className="flex flex-wrap gap-2">
                {JOB_TYPE_OPTIONS.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleJobType(type)}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                      preferredJobTypes.includes(type)
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                        : 'border-white/10 text-text-secondary hover:border-white/20'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expectedSalaryRange">Expected Salary Range</Label>
                <Input
                  id="expectedSalaryRange"
                  placeholder="e.g., $80,000 - $100,000"
                  {...register('expectedSalaryRange')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="availabilityDate">Availability Date</Label>
                <Input
                  id="availabilityDate"
                  type="date"
                  {...register('availabilityDate')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Additional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="source">Source</Label>
              <Select
                value={watch('source') || 'manual'}
                onValueChange={(value) => setValue('source', value, { shouldDirty: true, shouldValidate: true })}
              >
                <SelectTrigger id="source">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((src) => (
                    <SelectItem key={src} value={src}>
                      {src.charAt(0).toUpperCase() + src.slice(1).replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to="/candidates">Cancel</Link>
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Candidate'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
